import { React, useState, useEffect, useRef } from 'react';

// mui
import {
    Box,
    Collapse,
    Divider,
    Skeleton,
    Stack,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TextField,
} from '@mui/material';

// react-virtuoso
import { Virtuoso } from 'react-virtuoso';

// api
import {
    FilterSearchList,
    AdminUserSearch,
    AdminUserChangeStatus,
    AdminUserChangeType,
    AdminUserChangeVerified,
    AdminUserDelete,
} from '../../../api';

import { def } from '@goldblack/shared';

// client-shared
import { storage } from '@goldblack/client-storage';
import { ContainerScroll, FilterDescHeader, Message } from '@goldblack/client-comps';

// components
import {
    UserListItem,
    AccountUserInfoPanel,
    LoiseUserStatusDialog,
    LoiseUserTypeDialog,
    LoiseUserVerifiedDialog,
    LoiseUserDeleteDialog,
    AdminMenuBar,
} from '../..';

function AccountFilterItem({ filter, value, onChange }) {
    return (
        <TextField
            variant='standard'
            size='small'
            fullWidth
            autoComplete='off'
            spellCheck={false}
            value={value ?? ''}
            onChange={(e) => onChange(filter.name, e.target.value)}
        />
    );
}

function AccountFilterCollapse({ open, loading, filterData, filterObject, onFilterChange }) {
    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Collapse
                in={open}
                timeout='auto'
                unmountOnExit={false}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    maxHeight: '100%',
                    overflow: 'hidden',
                    '& .MuiCollapse-wrapper': {
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    '& .MuiCollapse-wrapperInner': {
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}>
                <Box
                    sx={{
                        height: '100%',
                        minHeight: 0,
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    <ContainerScroll sx={{ height: '100%', maxHeight: '100%', flex: 1, minHeight: 0 }}>
                        {loading ? (
                            <Skeleton variant='rectangular' height={26 * 2} sx={{ m: 1 }} />
                        ) : (
                            <TableContainer sx={{ p: 1 }}>
                                <Table padding='checkbox'>
                                    <TableBody>
                                        {filterData.map((filter) => (
                                            <TableRow key={filter._id}>
                                                <TableCell
                                                    sx={{
                                                        width: '20%',
                                                        borderBottom: 'none',
                                                        color: 'primary.main',
                                                    }}>
                                                    {filter.name}
                                                </TableCell>
                                                <TableCell align='right' sx={{ borderBottom: 'none' }}>
                                                    <AccountFilterItem
                                                        filter={filter}
                                                        value={filterObject[filter.name]}
                                                        onChange={onFilterChange}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </ContainerScroll>
                </Box>
            </Collapse>
        </Box>
    );
}

function AccountUsersSearch(props) {
    const { account } = storage.transience.getValue();
    const [openFilter, setOpenFilter] = useState(false);
    const [filterObject, setFilterObject] = useState({});
    const [filterDesc, setFilterDesc] = useState({});
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [typeValue, setTypeValue] = useState(-1);
    const [statusValue, setStatusValue] = useState(-1);
    const [verifiedValue, setVerifiedValue] = useState(false);
    const [openType, setOpenType] = useState(false);
    const [openStatus, setOpenStatus] = useState(false);
    const [openVerified, setVerified] = useState(false);
    const [openDelete, setDelete] = useState(false);
    const [data, setData] = useState({ loadmore: false, skip: 0, data: [] });
    const scrollParentRef = useRef(null);
    const virtuosoRef = useRef(null);
    const debounceRef = useRef(null);

    const getSelectedUser = () => (data.data.length > 0 ? data.data[selectedIndex] : null);

    const makeFilterDesc = (fo) => {
        const desc = {};
        for (const [key, val] of Object.entries(fo)) {
            if (val) desc[key.charAt(0).toUpperCase() + key.slice(1)] = val;
        }
        return desc;
    };

    const doSearch = (fo) => {
        setData({ loadmore: true, skip: 0, data: [] });
        AdminUserSearch('account', fo, 0, props.config?.searchLimit ?? 20, setData);
    };

    const onFilterChange = (name, value) => {
        const next = { ...filterObject, [name]: value };
        setFilterObject(next);
        setFilterDesc(makeFilterDesc(next));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(next), 700);
    };

    const loadMoreRows = () => {
        if (data.loadmore) {
            AdminUserSearch('account', filterObject, data.skip, props.config?.searchLimit ?? 20, setData);
        }
    };

    const onClickAdmin = () => {
        if (getSelectedUser()) setOpenType(true);
    };
    const onClickNormal = () => {
        if (getSelectedUser()) setOpenType(true);
    };
    const onClickBlock = () => {
        if (getSelectedUser()) {
            setOpenStatus(true);
            setStatusValue(def.UserStatus.BANNED);
        }
    };
    const onClickActive = () => {
        if (getSelectedUser()) {
            setOpenStatus(true);
            setStatusValue(def.UserStatus.ACTIVE);
        }
    };
    const onClickInactive = () => {
        if (getSelectedUser()) {
            setOpenStatus(true);
            setStatusValue(def.UserStatus.INACTIVE);
        }
    };
    const onClickVerified = () => {
        const user = getSelectedUser();
        if (user) {
            setVerified(true);
            setVerifiedValue(user.badges?.verified ?? false);
        }
    };
    const onClickDelete = () => {
        if (getSelectedUser()) setDelete(true);
    };

    const onClickStatusOk = (expire, forced, reason) => {
        setOpenStatus(false);
        const user = getSelectedUser();
        if (!user) return;
        switch (statusValue) {
            case def.UserStatus.ACTIVE:
            case def.UserStatus.BANNED:
            case def.UserStatus.INACTIVE:
                AdminUserChangeStatus(user._id, statusValue, expire, forced, reason);
                break;
        }
    };
    const onClickTypeOk = () => {
        setOpenType(false);
        AdminUserChangeType(getSelectedUser()._id, typeValue);
    };
    const onClickVerifiedOk = () => {
        setVerified(false);
        AdminUserChangeVerified(getSelectedUser()._id, !verifiedValue);
    };
    const onClickDeleteOk = () => {
        setDelete(false);
        const userId = getSelectedUser()._id;
        AdminUserDelete(userId, 'account', () => {
            setData((prev) => ({ ...prev, data: prev.data.filter((u) => u._id !== userId) }));
        });
    };

    const onCloseType = (e, reason) => {
        if (reason !== 'backdropClick') setOpenType(false);
    };
    const onCloseStatus = (e, reason) => {
        if (reason !== 'backdropClick') setOpenStatus(false);
    };
    const onCloseVerified = (e, reason) => {
        if (reason !== 'backdropClick') setVerified(false);
    };
    const onCloseDelete = (e, reason) => {
        if (reason !== 'backdropClick') setDelete(false);
    };

    useEffect(() => {
        if (account.filter.data.length === 0) {
            FilterSearchList('account', def.FilterGroup.ADMIN);
        }
        doSearch({});
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const apiItems = [{ label: 'Delete', onClick: onClickDelete }];

    const editItems = [
        { label: 'Set Admin', onClick: onClickAdmin },
        { label: 'Set Normal', onClick: onClickNormal },
        { label: 'Set Block', onClick: onClickBlock },
        { label: 'Set Active', onClick: onClickActive },
        { label: 'Set Inactive', onClick: onClickInactive },
        { label: 'Set Verified', onClick: onClickVerified },
    ];

    return (
        <Stack direction='column' sx={{ p: 0, m: 0, width: '100%', height: '87vh', minHeight: 0 }}>
            <AdminMenuBar apiItems={apiItems} editItems={editItems} />

            {/* Filter header + collapse */}
            <Box
                sx={{
                    flexShrink: 1,
                    minHeight: 0,
                    maxHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                <Box sx={{ flexShrink: 0 }}>
                    <FilterDescHeader
                        filterDesc={filterDesc}
                        open={openFilter}
                        onToggle={() => setOpenFilter((f) => !f)}
                    />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <AccountFilterCollapse
                        open={openFilter}
                        loading={account.filter.loading}
                        filterData={account.filter.data}
                        filterObject={filterObject}
                        onFilterChange={onFilterChange}
                    />
                </Box>
            </Box>

            <Divider orientation='horizontal' variant='fullWidth' sx={{ flexShrink: 0 }} />

            {/* User list + info panel */}
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                {/* Left column: list */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: 1,
                        borderColor: 'primary.main',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            borderBottom: 1,
                            borderColor: 'primary.main',
                            boxSizing: 'border-box',
                        }}>
                        <ContainerScroll ref={scrollParentRef}>
                            <Virtuoso
                                ref={virtuosoRef}
                                style={{ height: '100%' }}
                                data={data.data}
                                overscan={200}
                                increaseViewportBy={{ top: 100, bottom: 100 }}
                                itemContent={(index) => {
                                    const user = data.data[index];
                                    return (
                                        <UserListItem
                                            {...props}
                                            key={user._id}
                                            user={user}
                                            type='search'
                                            selected={index === selectedIndex}
                                            onClick={() => setSelectedIndex(index)}
                                        />
                                    );
                                }}
                                components={{
                                    EmptyPlaceholder: () => <Message variant='m_body1' message='No users' />,
                                }}
                                customScrollParent={scrollParentRef.current}
                                endReached={() => {
                                    if (data.loadmore) loadMoreRows();
                                }}
                            />
                        </ContainerScroll>
                    </Box>
                </Box>

                {/* Right column: info */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            borderBottom: 1,
                            borderColor: 'primary.main',
                            boxSizing: 'border-box',
                        }}>
                        <AccountUserInfoPanel user={getSelectedUser()} config={props.config} />
                    </Box>
                </Box>
            </Box>

            {/* Dialogs */}
            {openType && (
                <LoiseUserTypeDialog
                    open={openType}
                    user={getSelectedUser()}
                    onClose={onCloseType}
                    onOK={onClickTypeOk}
                    onCancel={onCloseType}
                    targetType={typeValue}
                />
            )}
            {openStatus && (
                <LoiseUserStatusDialog
                    open={openStatus}
                    user={getSelectedUser()}
                    onClose={onCloseStatus}
                    onOK={onClickStatusOk}
                    onCancel={onCloseStatus}
                    targetStatus={statusValue}
                />
            )}
            {openVerified && (
                <LoiseUserVerifiedDialog
                    open={openVerified}
                    user={getSelectedUser()}
                    onClose={onCloseVerified}
                    onOK={onClickVerifiedOk}
                    onCancel={onCloseVerified}
                />
            )}
            {openDelete && (
                <LoiseUserDeleteDialog
                    open={openDelete}
                    user={getSelectedUser()}
                    onClose={onCloseDelete}
                    onOK={onClickDeleteOk}
                    onCancel={onCloseDelete}
                />
            )}
        </Stack>
    );
}

export { AccountUsersSearch };
